import React from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import {
  animations,
  colors,
  mixins,
  shadows,
} from '../../core/styles/theme/default';
import { H4 } from '../../primitives';
import { AnimatedIconArrowRight } from '../../primitives/IconSVG';
import months from '../../core/i18n/months';

const StyledEvent = styled.article`
  background-color: ${colors.white};
  border: ${rem(1)} solid ${colors.grue300};
  border-radius: ${rem(4)};

  ${mixins.responsiveStyles({
    height: { zero: 'auto', medium: '100%' },
  })}

  ${({ theme }) => theme?.stylesExtension?.event?.event}
`;

const StyledEventLink = styled.a`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
  transition: box-shadow ${animations.default};
  height: 100%;

  ${mixins.responsiveStyles({
    padding: {
      zero: `${rem(15)} ${rem(15)} ${rem(15)} 0`,
      medium: `${rem(24)} 0`,
    },
  })}

  &:hover, &:focus {
    box-shadow: ${shadows.dropDownHover};
  }

  .m-richtext & {
    text-decoration: none;
  }
`;

const StyledDate = styled.time`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 ${rem(10)};

  ${mixins.responsiveStyles({
    width: {
      zero: rem(90),
      medium: rem(120),
      ultra: rem(160),
    },
  })}
`;

const StyledDayOfMonth = styled.div`
  line-height: 1;
  font-weight: 900;

  ${mixins.responsiveStyles({
    'font-size': { zero: rem(48), medium: rem(64), ultra: rem(80) },
  })}
`;

const StyledMonth = styled.div`
  line-height: 1;
  font-weight: 900;
  text-transform: uppercase;

  ${mixins.responsiveStyles({
    'font-size': { zero: rem(18), medium: rem(30) },
  })}
`;

const StyledEventContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const StyledEventDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${rem(5)};
  font-weight: 500;
  line-height: 1.7;

  ${mixins.responsiveStyles({
    'font-size': { zero: rem(16), medium: rem(18) },
  })}
`;

const StyledProvider = styled.div``;

const StyledTitle = styled(H4)`
  letter-spacing: 0;
  color: inherit;

  ${mixins.responsiveStyles({
    'font-size': { zero: rem(18), medium: rem(24), large: rem(24) },
    'line-height': { zero: '1.4', medium: '1.4' },
  })}
`;

const StyledLocation = styled.div``;

const StyledArrow = styled(AnimatedIconArrowRight)`
  width: ${rem(18)};
  height: ${rem(18)};
  flex-shrink: 0;

  ${mixins.responsiveStyles({
    margin: {
      zero: `${rem(12)} 0 0 ${rem(4)}`,
      medium: `0 ${rem(25)} 0 ${rem(40)}`,
    },
  })}
`;

const Event = (props: EventProps) => {
  const {
    provider,
    title,
    location,
    plz,
    language,
    eventDate,
    href,
    openInNewTab,
    node_locale: locale = 'de',
  } = props.data;
  const eventDay = parseInt(eventDate.split('-')[2], 10);
  const eventMonth = parseInt(eventDate.split('-')[1], 10);
  const address = [plz, location, language].filter(Boolean).join(' | ');

  return (
    <StyledEvent className="animate">
      <StyledEventLink
        href={href}
        title={title}
        target={openInNewTab ? '_blank' : '_self'}
      >
        <StyledDate>
          <StyledDayOfMonth>{eventDay}</StyledDayOfMonth>
          <StyledMonth>{months.short[locale][eventMonth - 1]}</StyledMonth>
        </StyledDate>
        <StyledEventContent>
          <StyledEventDetails>
            <StyledProvider>{provider}</StyledProvider>
            <StyledTitle>{title}</StyledTitle>
            {address && <StyledLocation>{address}</StyledLocation>}
          </StyledEventDetails>
          <StyledArrow />
        </StyledEventContent>
      </StyledEventLink>
    </StyledEvent>
  );
};

export type EventProps = {
  data: {
    __typename: string;
    node_locale: string;
    provider: string;
    title: string;
    location?: string;
    language?: string;
    plz?: string;
    eventDate: string;
    href: string;
    openInNewTab?: boolean;
  };
};

export default Event;
